import styles from "./emergency-fund.module.css"
import astyles from "./affordability.module.css"

import React from "react"
import cx from "classnames"
import {Wizard} from "activities/components/wizard"
import * as str from "shared/string"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"

const isFirstStepComplete = ({monthlyIncome, savingsGoal}) => (monthlyIncome > 0 && savingsGoal > 0 ? true : false)

const isSelected = (savingsGoal, amount, currentSavings) => amount !== 0 && (savingsGoal === amount || savingsGoal === amount - currentSavings)

const MonthlyIncomeAndGoalScreen = ({resetKey, updateState, monthlyIncome, savingsGoal, currentSavings, step}) => {
  const {details} = step
  const question = details.question
  const {optionsQuestion, goals} = details.options

  return (
    <div className={styles["screen"]}>
      <div id={`${str.toKebab(question)}`} className={cx(styles["question"], fonts.getFontClass())}>
        {question}
      </div>
      <inputs.LongCalcInput
        aria-labelledby={`${str.toKebab(question)}`}
        resetKey={resetKey}
        onChange={updateState("monthlyIncome", util.fromString)}
        integerLimit={5}
        initialValue={monthlyIncome}
      />
      <div className={styles["goals"]}>
        <div className={cx(styles["question"], fonts.getFontClass())}>{optionsQuestion}</div>
        {goals &&
          goals.map((g, idx) => {
            const amount = g.genAmount(monthlyIncome)
            const selected = isSelected(savingsGoal, amount, currentSavings)

            return (
              <button
                role="radio"
                aria-checked={selected}
                key={g.id}
                className={cx(styles["goal"], selected && styles["selected"])}
                onClick={() => updateState("savingsGoal")(amount)}
              >
                <div className={styles["goal-amount"]}>{util.formatDollarsNoCents(amount)}</div>
                <div className={styles["goal-label"]}>{g.text}</div>
              </button>
            )
          })}
      </div>
    </div>
  )
}

const CurrentSavingsScreen = ({resetKey, updateState, currentSavings, savingsGoal, step}) => {
  const {details} = step
  const question = details.question
  return (
    <div className={styles["screen"]}>
      <div id={`${str.toKebab(question)}`} className={cx(styles["question"], fonts.getFontClass())}>
        {question}
      </div>
      <inputs.LongCalcInput
        aria-labelledby={`${str.toKebab(question)}`}
        resetKey={resetKey}
        onChange={updateState("currentSavings", util.fromString)}
        integerLimit={5}
        initialValue={currentSavings}
      />
      <div className={styles["remaining"]}>
        <div className={cx(styles["question"], fonts.getFontClass())}>Remaining savings goal:</div>
        <div className={styles["remaining-amount"]}>{util.formatDollarsNoCents(util.ifNegative(savingsGoal - currentSavings))}</div>
      </div>
    </div>
  )
}

const onChangeCurrentSavingsScreen = (setState, props) => {
  const {savingsGoal, currentGoal, currentSavings} = props
  const newGoal = currentGoal + currentSavings === savingsGoal ? currentGoal : savingsGoal - currentSavings
  setState({currentGoal: newGoal < 0 ? 0 : newGoal})
}

const getTotalExpenses = (monthlyExpenses, yearlyExpenses) => {
  const monthly = monthlyExpenses.map((expense) => expense.cost).filter((x) => x !== undefined)
  const yearly = yearlyExpenses.map((expense) => expense.cost).filter((x) => x !== undefined)
  const totalMonthlyExpenses = monthly.reduce((acc, val) => acc + val)
  const totalYearlyExpenses = yearly.reduce((acc, val) => acc + val)
  return totalMonthlyExpenses + totalYearlyExpenses / 12
}

const getNetIncome = (monthlyExpenses, yearlyExpenses, monthlyIncome) => monthlyIncome - getTotalExpenses(monthlyExpenses, yearlyExpenses)

const MonthlyExpensesScreen = (props) => {
  const {resetKey, updateState, monthlyIncome, monthlyExpenses, yearlyExpenses, theme, setState, colorClaims, step} = props
  const {monthlyExpenseOptions, colors, yearlyExpenseOptions, monthlyIntegerLimit, yearlyIntegerLimit, lockedMonthlyOptions} = step.details
  const monthlyColors = util.mapColors(colorClaims, colors, "monthlyExpenses")
  const yearlyColors = util.mapColors(colorClaims, colors, "yearlyExpenses")
  return (
    <div className={astyles["budget-step"]}>
      <div className={astyles["large-selections"]}>
        <div className="selections large">
          <inputs.ExpenseForm
            className={astyles["form"]}
            containerClassName={astyles["expense-container"]}
            entryClassName={astyles["dropdown"]}
            title="Monthly Expenses"
            initialValue={monthlyExpenses}
            integerLimit={monthlyIntegerLimit}
            onRowAdd={util.addRow(setState, "monthlyExpenses")}
            onRowRemove={util.removeRow(setState, "monthlyExpenses")}
            onRowChange={util.updateRow(setState, "monthlyExpenses")}
            resetKey={resetKey}
            options={monthlyExpenseOptions}
            colors={monthlyColors}
            theme={theme}
            min={3}
            max={monthlyExpenseOptions.length}
            lockedOptions={lockedMonthlyOptions}
          />
          <inputs.ExpenseForm
            className={astyles["form"]}
            entryClassName={astyles["dropdown"]}
            containerClassName={astyles["expense-container"]}
            title="Yearly Expenses"
            initialValue={yearlyExpenses}
            integerLimit={yearlyIntegerLimit}
            onRowAdd={util.addRow(setState, "yearlyExpenses")}
            onRowRemove={util.removeRow(setState, "yearlyExpenses")}
            onRowChange={util.updateRow(setState, "yearlyExpenses")}
            resetKey={resetKey}
            options={yearlyExpenseOptions}
            colors={yearlyColors}
            theme={theme}
            min={2}
            max={yearlyExpenseOptions.length}
          />
        </div>
      </div>
      <callouts.EquationCallout>
        <callouts.EquationDetail label="Montlhy Income" result={util.formatDollarsNoCents(monthlyIncome)} />
        <callouts.EquationDetail label="Total Expenses" result={util.formatDollarsNoCents(getTotalExpenses(monthlyExpenses, yearlyExpenses))} operand="subtraction" />
        <callouts.EquationLine />
        <callouts.EquationDetail label="Available to Save" result={util.formatDollarsNoCents(getNetIncome(monthlyExpenses, yearlyExpenses, monthlyIncome))} />
      </callouts.EquationCallout>
    </div>
  )
}

const onChangeExpensesQuestion = (setState, props, name) => {
  const expenseIdx = props.monthlyExpenses.map((e) => e.name).indexOf(name)
  setState({projectedMonthlyPayment: props.monthlyExpenses[expenseIdx].cost})
}

const ResultsScreen = ({monthlyIncome, currentGoal, monthlyExpenses, yearlyExpenses}) => {
  const monthlySavings = getNetIncome(monthlyExpenses, yearlyExpenses, monthlyIncome)
  return (
    <div className={styles["results-screen"]}>
      <div className={cx(styles["results-question"], fonts.getFontClass())}>
        {monthlySavings <= 0 ? (
          <div>
            Hmm... you don't have enough left over to put toward an emergency fund. That's okay, you'll get there. You can find ways to save with the{" "}
            <a href="https://banzai.org/wellness/resources/trim-your-budget-coach">Trim Your Budget Coach</a>.
          </div>
        ) : (
          <div>
            Here's how long it will take you to reach your savings goal of <strong>{util.formatDollarsNoCents(currentGoal)}</strong>.
          </div>
        )}
      </div>
      <callouts.Callout className={styles["callout"]}>
        <callouts.Detail label="Monthly Savings" result={util.formatDollarsNoCents(monthlySavings)} resultStyles={styles["stat-result"]} />
        <callouts.Detail label="Months to Save" result={Math.ceil(currentGoal / monthlySavings)} resultStyles={styles["stat-result"]} />
      </callouts.Callout>
    </div>
  )
}

export default (props) => <EmergencyFundCalculator_ {...props} />

const EmergencyFundCalculator_ = shared.ActivityFactory(
  ({getProps, getState, updateState, setState, onReset}) => {
    const {resetKey, monthlyIncome, savingsGoal, currentSavings, currentGoal, monthlyExpenses, yearlyExpenses, colorClaims} = getState()
    const {steps, theme} = getProps()
    return (
      <div className={styles["wizard"]}>
        <Wizard
          className={styles["emergency"]}
          resetKey={resetKey}
          onReset={onReset}
          setup={{
            steps: steps,
            theme: theme,
            props: {
              monthlyIncome: monthlyIncome,
              savingsGoal: savingsGoal,
              currentSavings: currentSavings,
              currentGoal: currentGoal,
              monthlyExpenses: monthlyExpenses,
              yearlyExpenses: yearlyExpenses,
              colorClaims: colorClaims,
              setState: setState,
              updateState: updateState
            }
          }}
        />
      </div>
    )
  },
  {
    defaultProps: {
      className: styles["affordability-calculator"],
      isWizardCalculator: true,
      steps: [
        {
          title: "Emergency Fund Calculator",
          subtitle: "Enter your monthly net income and savings goal.",
          isStepComplete: (stepProps) => isFirstStepComplete(stepProps),
          content: MonthlyIncomeAndGoalScreen,
          details: {
            question: "Monthly income?",
            options: {
              optionsQuestion: "Choose a savings goal:",
              goals: [
                {
                  id: "3-month",
                  text: "3 months of net income",
                  genAmount: (income) => (income > 0 ? income * 3 : 0)
                },
                {
                  id: "6-month",
                  text: "6 months of net income",
                  genAmount: (income) => (income > 0 ? income * 6 : 0)
                }
              ]
            }
          },
          buttonText: "Next"
        },
        {
          title: "Emergency Fund Calculator",
          subtitle: "Narrow your savings goal by entering how much you've already saved. If you haven't started, enter 0.",
          isStepComplete: () => true,
          onChangeStep: (setState, stepProps) => onChangeCurrentSavingsScreen(setState, stepProps),
          content: CurrentSavingsScreen,
          details: {
            question: "Current savings?"
          },
          buttonText: "Next"
        },
        {
          title: "Emergency Fund Calculator",
          subtitle: "Okay, now divide your monthly income into a few expense categories.",
          isStepComplete: () => true,
          onChangeStep: (setState, stepProps) => onChangeExpensesQuestion(setState, stepProps, "Mortgage"),
          content: MonthlyExpensesScreen,
          details: {
            colors: [
              "#FFBB00",
              "#64D254",
              "#C4A8DF",
              "#FF91B0",
              "#FF4444",
              "#6EC5EF",
              "#005F8B",
              "#00BDDC",
              "#65DFC5",
              "#9E6CC7",
              "#FF881F",
              "#FFE7AF",
              "#00637A",
              "#FFCBCF",
              "#006AF9",
              "#FFA971",
              "#EDFCAE",
              "#A7DD00",
              "#077259",
              "#A8AFDA"
            ],
            monthlyExpenseOptions: ["Utilities", "Phone", "Internet", "Car Payment", "Car Insurance", "Car Repairs", "Gas", "Food", "Entertainment", "Clothing", "Debt"],
            yearlyExpenseOptions: ["Life Insurance", "Medical", "Vacation", "Furniture", "Gifts", "Other"],
            monthlyIntegerLimit: 5,
            yearlyIntegerLimit: 6
          },
          buttonText: "Finish"
        },
        {
          title: "Emergency Fund Calculator",
          subtitle: "Take a look at the results!",
          isStepComplete: () => true,
          content: ResultsScreen,
          buttonText: "Start Over"
        }
      ]
    },
    stateFilter: (state) => state,
    defaultState: {
      colorClaims: [],
      monthlyIncome: 0,
      savingsGoal: 0,
      currentSavings: 2000,
      currentGoal: 0,
      monthlyExpenses: [
        {name: "Mortgage", cost: 1800},
        {name: "Food", cost: 250},
        {name: "Entertainment", cost: 50},
        {name: "Car Payment", cost: 230}
      ],
      yearlyExpenses: [
        {name: "Medical", cost: 3000},
        {name: "Gifts", cost: 200},
        {name: "Vacation", cost: 1000}
      ]
    }
  }
)
